import { useState } from 'react';
import { Compass, GraduationCap, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCatalog } from '../lib/data';
import SiteHeader from './SiteHeader';
import TrainEnvCard from './TrainEnvCard';
import ThisWeekCard from './ThisWeekCard';
import YourPath from './YourPath';
import { TrackCard } from './Home';

/**
 * The candidate ("Aspirant") home — a private, encouraging onboarding surface.
 * It shows ONLY the trails granted to this candidate, served through the
 * access-checked API (useCatalog → GET /api/catalog), never a direct Firestore
 * read (the rules deny candidates those). NO Dangal, NO admin, NO activity feed,
 * no peer standings — the candidate never sees the internal arena.
 */
export default function CandidateHome() {
  const { user, profile } = useAuth();
  const firstName = (profile?.displayName || '').split(' ')[0] || 'there';

  return (
    <div className="min-h-screen bg-paper-50 dark:bg-clay-floor">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-4 pb-16 pt-6">
        <div className="animate-rise-in">
          <h1 className="text-xl font-semibold text-ink-900 dark:text-paper-50">
            Welcome, {firstName}.
          </h1>
          <p className="mt-0.5 text-sm text-ink-500 dark:text-paper-300">
            You've been invited to explore Henoyo. Work through your kushtis at your own pace — every
            paintra earns you Zor (<span lang="hi">ज़ोर</span>).
          </p>
        </div>

        {profile?.mentor && (
          <div className="mt-4 flex items-center gap-3 rounded-xl border border-rule bg-paper-0 px-4 py-3 shadow-sm dark:border-clay-rule dark:bg-clay-surface">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-verdant-700 text-[11px] font-semibold text-white">
              {profile.mentor.initials}
            </span>
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                <GraduationCap className="h-3.5 w-3.5 text-terracotta-500" />
                Your Ustad
              </p>
              <p className="truncate text-sm font-medium text-ink-900 dark:text-paper-50">
                {profile.mentor.displayName}
              </p>
            </div>
          </div>
        )}

        <TrainEnvCard />
        <ThisWeekCard />
        <YourPath uid={user!.uid} candidate={true} />

        <section className="mt-6">
          <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
            <Compass className="h-3.5 w-3.5 text-terracotta-500" />
            Your kushtis
          </div>
          <CandidateTrailList uid={user?.uid} />
        </section>

        <p className="mt-8 flex items-center justify-center gap-1.5 text-center text-xs text-ink-400">
          <Sparkles className="h-3.5 w-3.5 text-terracotta-400" />
          New kushtis may be added as you go.
        </p>
      </main>
    </div>
  );
}

function CandidateTrailList({ uid }: { uid: string | undefined }) {
  const { tracks, loading } = useCatalog();
  const [activeTag, setActiveTag] = useState<string | null>(null);

  if (loading) {
    return <div className="py-10 text-center text-sm text-ink-400">Loading your kushtis…</div>;
  }
  if (tracks.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-rule p-8 text-center text-sm text-ink-400 bg-paper-100/50 dark:border-clay-rule dark:bg-clay-surface/60">
        No kushtis yet. Your kushtis will appear here once they're assigned.
      </div>
    );
  }

  const allTags = Array.from(new Set(tracks.flatMap((t) => t.tags ?? [])));
  const filtered = activeTag ? tracks.filter((t) => t.tags?.includes(activeTag)) : tracks;

  return (
    <div>
      {allTags.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          <button
            onClick={() => setActiveTag(null)}
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition ${
              activeTag === null
                ? 'bg-terracotta-500 text-white'
                : 'bg-paper-100 text-ink-500 hover:bg-paper-200 dark:bg-clay-raised dark:text-paper-300 dark:hover:bg-clay-surface'
            }`}
          >
            All
          </button>
          {allTags.map((tag) => (
            <button
              key={tag}
              onClick={() => setActiveTag(activeTag === tag ? null : tag)}
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition ${
                activeTag === tag
                  ? 'bg-terracotta-500 text-white'
                  : 'bg-paper-100 text-ink-500 hover:bg-paper-200 dark:bg-clay-raised dark:text-paper-300 dark:hover:bg-clay-surface'
              }`}
            >
              #{tag}
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-col gap-3">
        {filtered.map((t) => (
          <TrackCard key={t.id} uid={uid || ''} track={t} />
        ))}
      </div>
    </div>
  );
}
